import api from "./api";

/** Backend: EmailTemplate — name, description, html_content */
export type EmailTemplateApi = {
  id: number | string;
  name: string;
  description?: string | null;
  html_content?: string | null;
  company_id?: number | null;
};

/** Backend: SmsTemplate — name, text_content, char_limit */
export type SmsTemplateApi = {
  id: number | string;
  name: string;
  text_content?: string | null;
  char_limit?: number | null;
  company_id?: number | null;
};

function extractArray(payload: unknown): any[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const o = payload as Record<string, unknown>;
    const d = o.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === "object" && Array.isArray((d as Record<string, unknown>).data)) {
      return (d as { data: any[] }).data;
    }
  }
  return [];
}

function extractRecord(payload: unknown): any | null {
  if (!payload || typeof payload !== "object") return null;
  const o = payload as Record<string, unknown>;
  const d = o.data;
  if (d && typeof d === "object" && !Array.isArray(d)) return d as Record<string, unknown>;
  return null;
}

export function mapEmailTemplateFromApi(row: EmailTemplateApi) {
  return {
    id: String(row.id),
    name: row.name ?? "",
    description: row.description ?? undefined,
    html: row.html_content ?? "",
  };
}

export function mapSmsTemplateFromApi(row: SmsTemplateApi) {
  return {
    id: String(row.id),
    name: row.name ?? "",
    text: row.text_content ?? "",
    charLimit: row.char_limit ?? 160,
  };
}

export async function fetchEmailTemplates() {
  const res = await api.get("/admin/email-templates");
  return extractArray(res.data).map((row) => mapEmailTemplateFromApi(row as EmailTemplateApi));
}

export async function fetchSmsTemplates() {
  const res = await api.get("/admin/sms-templates");
  return extractArray(res.data).map((row) => mapSmsTemplateFromApi(row as SmsTemplateApi));
}

export async function fetchEmailTemplateById(id: string) {
  const res = await api.get(`/admin/email-templates/${id}`);
  const row = extractRecord(res.data) ?? (res.data as EmailTemplateApi);
  if (!row || row.id == null) return null;
  return mapEmailTemplateFromApi(row as EmailTemplateApi);
}

export async function fetchSmsTemplateById(id: string) {
  const res = await api.get(`/admin/sms-templates/${id}`);
  const row = extractRecord(res.data) ?? (res.data as SmsTemplateApi);
  if (!row || row.id == null) return null;
  return mapSmsTemplateFromApi(row as SmsTemplateApi);
}

export async function createEmailTemplate(payload: {
  name: string;
  description: string;
  html_content: string;
}) {
  const res = await api.post("/admin/email-templates", payload);
  const row = extractRecord(res.data) ?? res.data?.data;
  if (!row) throw new Error("Invalid create email template response");
  return mapEmailTemplateFromApi(row as EmailTemplateApi);
}

export async function updateEmailTemplate(
  id: string,
  payload: Partial<{ name: string; description: string; html_content: string }>,
) {
  const res = await api.put(`/admin/email-templates/${id}`, payload);
  const row = extractRecord(res.data) ?? res.data?.data;
  if (!row) throw new Error("Invalid update email template response");
  return mapEmailTemplateFromApi(row as EmailTemplateApi);
}

export async function deleteEmailTemplate(id: string) {
  await api.delete(`/admin/email-templates/${id}`);
}

export async function createSmsTemplate(payload: {
  name: string;
  text_content: string;
  char_limit: number;
}) {
  const res = await api.post("/admin/sms-templates", payload);
  const row = extractRecord(res.data) ?? res.data?.data;
  if (!row) throw new Error("Invalid create SMS template response");
  return mapSmsTemplateFromApi(row as SmsTemplateApi);
}

export async function updateSmsTemplate(
  id: string,
  payload: Partial<{ name: string; text_content: string; char_limit: number }>,
) {
  const res = await api.put(`/admin/sms-templates/${id}`, payload);
  const row = extractRecord(res.data) ?? res.data?.data;
  if (!row) throw new Error("Invalid update SMS template response");
  return mapSmsTemplateFromApi(row as SmsTemplateApi);
}

export async function deleteSmsTemplate(id: string) {
  await api.delete(`/admin/sms-templates/${id}`);
}

// --- Marketing campaigns (same API client / response shapes as templates) ---

/** Raw campaign row from Laravel admin API */
export type CampaignApiRow = {
  id: number | string;
  name: string;
  description?: string | null;
  customer_ids?: number[] | string[] | null;
  customers?: Array<{ id?: number | string; customer_id?: number | string }> | null;
  email_template_id?: number | string | null;
  sms_template_id?: number | string | null;
  credit_used?: number | string | null;
  use_all_customers?: boolean | number | string | null;
};

export type CampaignCore = {
  id: string;
  name: string;
  description: string;
  selectedCustomerIds: string[];
  useAllCustomers: boolean;
  emailTemplateId?: string | null;
  smsTemplateId?: string | null;
};

function isTruthyUseAll(value: CampaignApiRow["use_all_customers"]): boolean {
  if (value === true || value === 1 || value === "1") return true;
  return false;
}

function customerIdsFromRow(row: CampaignApiRow): string[] {
  if (Array.isArray(row.customer_ids)) {
    return row.customer_ids.map((id) => String(id));
  }
  if (Array.isArray(row.customers)) {
    return row.customers
      .map((c) => {
        const cid = c.id ?? c.customer_id;
        return cid != null ? String(cid) : "";
      })
      .filter(Boolean);
  }
  return [];
}

/** True when the stored selection covers every known customer (same as “All customers” in UI). */
export function deriveUseAllCustomers(
  selectedCustomerIds: string[],
  customers: { id: string }[],
): boolean {
  if (!customers.length || !selectedCustomerIds.length) return false;
  if (selectedCustomerIds.length !== customers.length) return false;
  const set = new Set(selectedCustomerIds);
  return customers.every((c) => set.has(c.id));
}

function normalizeCampaignRow(raw: unknown): CampaignApiRow {
  const r = raw as Record<string, unknown>;
  return {
    id: r.id as number | string,
    name: (r.name as string) ?? "",
    description: (r.description as string | null | undefined) ?? null,
    customer_ids: (r.customer_ids as number[] | string[] | null | undefined) ?? null,
    customers: (r.customers as CampaignApiRow["customers"]) ?? null,
    email_template_id: (r.email_template_id as number | string | null | undefined) ?? null,
    sms_template_id: (r.sms_template_id as number | string | null | undefined) ?? null,
    credit_used: (r.credit_used as number | string | null | undefined) ?? null,
    use_all_customers: (r.use_all_customers as CampaignApiRow["use_all_customers"]) ?? null,
  };
}

export function mapCampaignFromApi(row: CampaignApiRow, customers: { id: string }[]): CampaignCore {
  const fromApi = customerIdsFromRow(row);
  const useAllFlag = isTruthyUseAll(row.use_all_customers);
  const selectedCustomerIds =
    useAllFlag && customers.length ? customers.map((c) => c.id) : fromApi;

  const useAllCustomers = useAllFlag || deriveUseAllCustomers(selectedCustomerIds, customers);

  return {
    id: String(row.id),
    name: row.name ?? "",
    description: row.description ?? "",
    selectedCustomerIds,
    useAllCustomers,
    emailTemplateId:
      row.email_template_id != null && row.email_template_id !== ""
        ? String(row.email_template_id)
        : null,
    smsTemplateId:
      row.sms_template_id != null && row.sms_template_id !== ""
        ? String(row.sms_template_id)
        : null,
  };
}

export async function fetchCampaigns(): Promise<CampaignApiRow[]> {
  const res = await api.get("/admin/campaigns");
  return extractArray(res.data).map((row) => normalizeCampaignRow(row));
}

export async function fetchCampaignById(id: string): Promise<CampaignApiRow | null> {
  try {
    const res = await api.get(`/admin/campaigns/${id}`);
    const rec = extractRecord(res.data) ?? (res.data as Record<string, unknown>);
    if (!rec || rec.id == null) return null;
    return normalizeCampaignRow(rec);
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw e;
  }
}

export async function createCampaign(payload: {
  name: string;
  description: string;
  customer_ids: number[];
  credit_used: number;
  email_template_id?: number;
  sms_template_id?: number;
}): Promise<void> {
  await api.post("/admin/campaigns", payload);
}

export async function updateCampaignApi(
  id: string,
  payload: {
    name: string;
    description: string;
    customer_ids: number[];
    credit_used: number;
    email_template_id?: number;
    sms_template_id?: number;
  },
): Promise<void> {
  await api.put(`/admin/campaigns/${id}`, payload);
}

export async function deleteCampaign(id: string): Promise<void> {
  await api.delete(`/admin/campaigns/${id}`);
}

/** Dedicated route so Laravel does not treat `run` as a campaign id on `/admin/campaigns/run`. */
export type RunCampaignResult = "success" | "partial" | "failed";

export type RunCampaignResponse = {
  message?: string;
  data?: {
    result?: RunCampaignResult;
    campaign_id?: number | string;
    emails_attempted?: number;
    emails_sent?: number;
    emails_failed?: number;
    sms_attempted?: number;
    sms_sent?: number;
    sms_failed?: number;
    credits_used?: number;
    errors?: unknown[];
  };
};

export async function runCampaign(id: string): Promise<RunCampaignResponse> {
  const res = await api.get<RunCampaignResponse>("/admin/campaigns/run", { params: { id } });
  return res.data ?? {};
}

export type CampaignScheduleApiRow = {
  id: number | string;
  campaign_id: number | string;
  trigger_type: "appointment" | "consultation";
  frequency: "one_time" | "daily" | "weekly" | "bi_weekly" | "monthly" | "custom_days";
  timing: "before" | "on_day" | "after";
  days_offset?: number | null;
  day_of_month?: number | null;
  days?: string[] | null;
  is_active?: boolean | number | string;
};

export type CampaignSchedulePayload = {
  campaign_id: number;
  trigger_type: "appointment" | "consultation";
  frequency: "one_time" | "daily" | "weekly" | "bi_weekly" | "monthly" | "custom_days";
  timing: "before" | "on_day" | "after";
  days_offset?: number;
  day_of_month?: number;
  days?: string[];
  is_active?: boolean;
};

function normalizeCampaignScheduleRow(raw: unknown): CampaignScheduleApiRow {
  const row = raw as Record<string, unknown>;
  return {
    id: row.id as number | string,
    campaign_id: row.campaign_id as number | string,
    trigger_type: (row.trigger_type as CampaignScheduleApiRow["trigger_type"]) ?? "appointment",
    frequency: (row.frequency as CampaignScheduleApiRow["frequency"]) ?? "weekly",
    timing: (row.timing as CampaignScheduleApiRow["timing"]) ?? "before",
    days_offset: (row.days_offset as number | null | undefined) ?? null,
    day_of_month: (row.day_of_month as number | null | undefined) ?? null,
    days: (row.days as string[] | null | undefined) ?? null,
    is_active: (row.is_active as boolean | number | string | undefined) ?? true,
  };
}

export async function fetchCampaignSchedules(): Promise<CampaignScheduleApiRow[]> {
  const res = await api.get("/admin/campaign-schedules");
  return extractArray(res.data).map((row) => normalizeCampaignScheduleRow(row));
}

export async function createCampaignSchedule(
  payload: CampaignSchedulePayload
): Promise<CampaignScheduleApiRow> {
  const res = await api.post("/admin/campaign-schedules", payload);
  const row = extractRecord(res.data) ?? (res.data?.data as Record<string, unknown> | undefined);
  if (!row) throw new Error("Invalid create campaign schedule response");
  return normalizeCampaignScheduleRow(row);
}

export async function updateCampaignSchedule(
  id: string,
  payload: CampaignSchedulePayload
): Promise<CampaignScheduleApiRow> {
  const res = await api.put(`/admin/campaign-schedules/${id}`, payload);
  const row = extractRecord(res.data) ?? (res.data?.data as Record<string, unknown> | undefined);
  if (!row) throw new Error("Invalid update campaign schedule response");
  return normalizeCampaignScheduleRow(row);
}

export async function deleteCampaignSchedule(id: string): Promise<void> {
  await api.delete(`/admin/campaign-schedules/${id}`);
}

export function formatApiErrors(err: unknown): string {
  const anyErr = err as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } };
  const errors = anyErr?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    const parts = Object.values(errors).flat().filter(Boolean);
    if (parts.length) return parts.join("\n");
  }
  return anyErr?.response?.data?.message || "Something went wrong. Please try again.";
}
